<template>
  <div id="app" class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <!-- Skip Link for Accessibility -->
    <a href="#main-content" class="skip-link">
      Skip to main content
    </a>

    <!-- Loading Screen -->
    <div v-if="authStore.loading && !authStore.initialized" class="flex items-center justify-center min-h-screen">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
    </div>

    <!-- Idle warning modal -->
    <div v-if="idle.show" class="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
      <div class="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
        <h3 class="text-xl font-semibold text-slate-900">You're about to be signed out</h3>
        <p class="mt-2 text-slate-600">For your security, you'll be signed out due to inactivity in <span class="font-semibold">{{ idle.remaining }}</span> seconds.</p>
        <div class="mt-6 flex gap-3 justify-end">
          <button @click="staySignedIn" class="px-4 py-2 rounded-md text-white bg-gradient-to-r from-[#5C2A6D] to-[#2A3B6D] hover:opacity-95">Stay signed in</button>
          <button @click="signOutNow" class="px-4 py-2 rounded-md text-slate-700 border border-slate-300">Sign out now</button>
        </div>
      </div>
    </div>

    <!-- Main App -->
    <div v-else>
      <HoldingPage v-if="isMaintenanceMode" />
      <template v-else>
        <AppHeader :key="authStore.isAuthenticated ? 'authenticated' : 'guest'" />
        
        <main id="main-content" class="flex-1">
          <router-view />
        </main>
        
        <AppFooter />
      </template>
    </div>

    <!-- Toast/Notification Container -->
    <div id="notification-container" class="fixed top-4 right-4 z-50 space-y-2"></div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, reactive } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useSessionMonitor } from '@/composables/useSessionMonitor'
import { useIdleLogout } from '@/composables/useIdleLogout'
import { useUIStateMonitor } from '@/composables/useUIStateMonitor'
import AppHeader from '@/components/layout/AppHeader.vue'
import AppFooter from '@/components/layout/AppFooter.vue'
import HoldingPage from '@/views/HoldingPage.vue'

const authStore = useAuthStore()
const isMaintenanceMode = import.meta.env.VITE_APP_MAINTENANCE_MODE === 'true' || import.meta.env.VITE_APP_MAINTENANCE_MODE === true

// Start session monitoring
useSessionMonitor()

// Start UI state monitoring to prevent menu disappearing
useUIStateMonitor()

// Start idle logout monitor (defaults to VITE_SESSION_IDLE_MINUTES or 30)
useIdleLogout()

// Idle warning modal state and handlers
const idle = reactive({ show: false, remaining: 0 })
const onWarning = (e: Event) => {
  const detail = (e as CustomEvent).detail || {}
  idle.remaining = detail.remainingSeconds ?? 90
  idle.show = true
}
const onHide = () => { idle.show = false }

const staySignedIn = () => {
  window.dispatchEvent(new CustomEvent('teamio-idle-stay'))
  idle.show = false
}
const signOutNow = () => {
  idle.show = false
  const authStore = useAuthStore()
  
  console.log('🚪 Idle logout - starting sign out process...')
  
  // Clear local state immediately
  authStore.clearUserSession()
  console.log('✅ Idle logout - local state cleared')
  
  // Sign out from Supabase in background (don't wait)
  authStore.signOut().catch(error => {
    console.warn('⚠️ Idle logout sign out error (will redirect anyway):', error)
  })
  
  // Force IMMEDIATE hard redirect to login page with full URL
  console.log('🔄 Idle logout - forcing IMMEDIATE redirect to login page...')
  
  // Use the origin + path for absolute certainty
  const loginUrl = window.location.origin + '/login'
  console.log('🔄 Redirecting to:', loginUrl)
  
  // Use href for maximum compatibility - this WILL reload the page
  window.location.href = loginUrl
}

// Debug logging for maintenance mode
console.log('🔍 Maintenance Mode Check:', {
  VITE_APP_MAINTENANCE_MODE: import.meta.env.VITE_APP_MAINTENANCE_MODE,
  type: typeof import.meta.env.VITE_APP_MAINTENANCE_MODE,
  isMaintenanceMode,
  allEnvVars: Object.keys(import.meta.env).filter(key => key.startsWith('VITE_'))
})

onMounted(async () => {
  window.addEventListener('teamio-idle-warning', onWarning as EventListener)
  window.addEventListener('teamio-idle-hide', onHide as EventListener)
  await authStore.initialize()
})

onUnmounted(() => {
  window.removeEventListener('teamio-idle-warning', onWarning as EventListener)
  window.removeEventListener('teamio-idle-hide', onHide as EventListener)
})
</script>